import React, { useCallback, useState } from 'react';

import { JRCFileAvatar } from 'components/JRCFile/JRCFileAvatar';
import { resolveFileMimetype, UserDownload } from 'jamespot-user-api';

import styled from 'styled-components';
import { JRCTypography } from '../../JRCTypography/JRCTypography';
import { formatFileSize } from '../../../utils/utils.files';
import { JRCFlexBox } from '../../JRCFlex/JRCFlexBox';
import { JRCButtonDropdown } from '../../JRCButtonDropdown/JRCButtonDropdown';
import { useIntl } from 'react-intl';
import jamespot from 'jamespot-user-api';
import { JRCLoader } from '../../JRCLoader/JRCLoader';
import { JRCModalLayout } from 'components/JRCModal/JRCModalLayout';
import { JRCModalContent } from '../../JRCModal/JRCModalContent';
import { JRCAvatar } from '../../JRCAvatar/JRCAvatar';
import { Typography } from '../../JRCTypography/JRCTypography.styles';
import { JRCStyledHref } from '../../JRCHref/JRCHref';
import { JRCIconButton } from '../../JRCIconButton/JRCIconButton';
import { JRCEllipsis } from '../../JRCEllipsis/JRCEllipsis';

const Container = styled.div<{ inline?: boolean }>`
    ${({ inline }) =>
        inline
            ? `
        gap: 8px;
        display: flex;
        flex-wrap: wrap;
    `
            : ''}
`;

export const CSSWidgetAttachment = styled.div<{ inline?: boolean }>`
    display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
    align-items: center;
    gap: 12px;
    cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
    width: 100%;
    padding-right: 12px;
    height: fit-content;
    border-radius: ${({ theme }) => theme.space.sm}px;

    ${({ inline, theme, onClick }) =>
        inline
            ? `
        padding: 4px 12px 4px 4px;
        border-radius: 10px;
        background-color: ${theme.color.grey0};
        transition: background-color 100ms;
        &:hover {
            ${onClick && `background-color: ${theme.color.grey1};`}
        }
    `
            : `
        margin-bottom: 8px;
        ${
            onClick &&
            `
        &:hover {
            background-color: ${theme.color.grey0};
            font-weight: 600;
        }
        `
        }
    `}
}
`;

export type JRCWidgetAttachmentProps = {
    files: {
        id: number;
        uri: string | undefined;
        mimetype: string;
        title: string;
        size: number;
    }[];
    fileDisplay?: 'list';
    onDelete?: (idFile: number) => Promise<void>;
    onError?: (message?: string) => void;
    mocks?: {
        fileGetDownloads: typeof jamespot.file.getDownload;
        fileGeneratePreview: typeof jamespot.file.generatePreview;
    };
    mode?: 'view' | 'edit';
};

const LoaderWrapper = styled.div`
    margin-bottom: 24px;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const DownloadsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
`;

const ActionWrapper = styled.div`
    margin-right: ${({ theme }) => theme.space.sm}px;
    margin-bottom: ${({ theme }) => theme.space.sm}px;
    display: flex;
    align-items: center;
`;

/**
 * TODO: resolveFileMimetype should be done before
 */
export const JRCWidgetAttachment = ({
    files,
    inline,
    onClick,
    onDelete,
    onError,
    mocks,
    mode = 'view',
}: JRCWidgetAttachmentProps & {
    onClick: (idFile: number) => void;
    inline?: boolean;
}) => {
    const intl = useIntl();
    const [isDownloadsModalOpen, setDownloadsModalOpen] = useState(false);
    const [downloads, setDownloads] = useState<UserDownload[]>();
    const [loadingFiles, setLoadingFiles] = useState<number[]>([]);

    const _fileGetDownloads = useCallback(
        (id: number) => (mocks?.fileGetDownloads ? mocks.fileGetDownloads(id) : jamespot.file.getDownload(id)),
        [mocks],
    );

    const openDownloads = useCallback(
        (fileId: number) => {
            setDownloadsModalOpen(true);
            _fileGetDownloads(fileId)
                .then((res) => {
                    setDownloads(res.result.list);
                })
                .catch((e: any) => {
                    if (onError) {
                        onError(e.errorMsg);
                    }
                });
        },
        [_fileGetDownloads, onError],
    );

    const closeDownloads = useCallback(() => {
        setDownloadsModalOpen(false);
        setDownloads(undefined);
    }, []);

    return (
        <>
            <Container inline={inline}>
                {files &&
                    files.map((file) => {
                        return (
                            <JRCFlexBox gap={6} key={file.uri}>
                                <CSSWidgetAttachment
                                    role={'button'}
                                    onClick={mode === 'view' ? () => onClick(file.id) : undefined}
                                    key={file.id}>
                                    <JRCFileAvatar uri={file.uri} size={40} mimetype={resolveFileMimetype(file)} />
                                    <JRCEllipsis length={25} label={file.title}></JRCEllipsis>
                                    <JRCTypography size={'s'} color={'grey4'}>
                                        ({formatFileSize(file.size)})
                                    </JRCTypography>
                                </CSSWidgetAttachment>
                                {mode === 'view' ? (
                                    <ActionWrapper>
                                        <JRCButtonDropdown
                                            variant="link"
                                            small={true}
                                            icon="icon-ellipsis-v"
                                            color="primary"
                                            label={file.title}
                                            smallOpenDirection={'left'}
                                            options={[
                                                {
                                                    label: intl.formatMessage({ id: 'GLOBAL_Download' }),
                                                    onClick: () =>
                                                        window.open(`/downloadFile.php?id=${file.id}`, '_self'),
                                                },
                                                {
                                                    label: intl.formatMessage({ id: 'ATTACHMENTS_See_Downloads' }),
                                                    onClick: () => openDownloads(file.id),
                                                },
                                                ...(onDelete
                                                    ? [
                                                          {
                                                              label: intl.formatMessage({ id: 'GLOBAL_Delete' }),
                                                              onClick: () => onDelete(file.id),
                                                          },
                                                      ]
                                                    : []),
                                            ]}
                                        />
                                    </ActionWrapper>
                                ) : loadingFiles.includes(file.id) ? (
                                    <ActionWrapper>
                                        <JRCLoader size={'s'} />
                                    </ActionWrapper>
                                ) : (
                                    <JRCIconButton
                                        icon={'icon-times'}
                                        onClick={() => {
                                            if (onDelete) {
                                                setLoadingFiles((prev) => [...prev, file.id]);
                                                onDelete(file.id).finally(() => {
                                                    setLoadingFiles((prev) => prev.filter((id) => id !== file.id));
                                                });
                                            }
                                        }}
                                        color={'primary'}
                                    />
                                )}
                            </JRCFlexBox>
                        );
                    })}
            </Container>
            <JRCModalLayout
                closeHandler={closeDownloads}
                enableClickAwayCloseModal
                open={isDownloadsModalOpen}
                variant={'fixed-height'}
                maxHeight={'800px'}>
                <JRCModalContent
                    title={intl.formatMessage({ id: 'GLOBAL_Downloads' }) + (downloads ? ` (${downloads.length})` : '')}
                    overflow={'scroll'}
                    onClickIconClose={closeDownloads}>
                    {downloads ? (
                        <DownloadsWrapper>
                            {downloads.length > 0 ? (
                                downloads.map((d, i) => (
                                    <JRCFlexBox key={`visitor-${i}`} y={'center'} gap={24}>
                                        <JRCAvatar size={5} uri={`${d.type}/${d.idUser}`} />
                                        <JRCStyledHref href={`/${d.type}/${d.idUser}`}>
                                            <Typography>{d.Pseudo}</Typography>
                                        </JRCStyledHref>
                                    </JRCFlexBox>
                                ))
                            ) : (
                                <JRCTypography align={'center'}>
                                    {intl.formatMessage({ id: 'ATTACHMENTS_No_Download' })}
                                </JRCTypography>
                            )}
                        </DownloadsWrapper>
                    ) : (
                        <LoaderWrapper>
                            <JRCLoader size={'m'} />
                        </LoaderWrapper>
                    )}
                </JRCModalContent>
            </JRCModalLayout>
        </>
    );
};
